﻿import { useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomInputText from "@/components/desktop/form/input/input-text/CustomInputText";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";

import { useProductDetail } from "@/hooks/products/useProductDetail";

import styles from "./modal-product-detail.module.scss";

function selectByCustomerPriority(items, makeVisibleLabel) {
	if (!Array.isArray(items)) return [];
	const itemsWithCustomer = items.filter((item) => item?.customer_id !== null);
	const base = itemsWithCustomer.length > 0 ? itemsWithCustomer : items.filter((item) => item?.customer_id === null);

	const seen = new Set();
	const result = [];

	for (const item of base) {
		const rawLabel = (item?.label || "").trim();
		if (!rawLabel) continue;

		const visibleLabel = typeof makeVisibleLabel === "function" ? makeVisibleLabel(item) : rawLabel;

		if (seen.has(visibleLabel)) continue;
		seen.add(visibleLabel);

		result.push({
			value: item?.value ?? rawLabel,
			label: visibleLabel,
		});
	}

	return result;
}

const ModalProductDetail = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const productAction = searchParams.get("action");
	const productCode = searchParams.get("product");
	const modalIsOpen = Boolean(productAction === "detail" && productCode);

	const modalTitle = "Detalle del Producto";
	const { data: productData, isLoading } = useProductDetail({ product: modalIsOpen ? productCode : null });
	const [showViewer, setShowViewer] = useState(true);

	const handleClose = () => {
		const params = new URLSearchParams(location.search);
		params.delete("product");
		params.delete("action");
		const nextSearch = params.toString();
		navigate(
			{
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : "",
				hash: location.hash,
			},
			{ replace: true },
		);
	};

	const computed = useMemo(() => {
		const current = productData || {};

		const selectedAptitudes = selectByCustomerPriority(current?.aptitudes, (item) => (item?.label || "").trim());

		const selectedActiveIngredients = selectByCustomerPriority(current?.active_ingredients, (item) => {
			const label = (item?.label || "").trim();
			const concentration = (item?.concentration || "").trim();
			return concentration ? `${label} (${concentration})` : label;
		});

		return {
			id: current?.id ?? "",
			tradename: current?.tradename ?? "",
			code: current?.code ?? "",
			url_tag: current?.url_tag ?? "",
			selectedAptitudes,
			selectedActiveIngredients,
		};
	}, [productData]);

	return (
		<CustomModalForm open={modalIsOpen} onClose={handleClose} title={modalTitle} size="xxl" asForm isLoading={isLoading} gridTable={true} showSave={false}>
			{isLoading ? (
				<CustomFetching center size="lg" />
			) : (
				<>
					<CustomInputText label="Nombre" name="name" value={computed.tradename} readOnly={true} col={6} />
					<CustomInputText label="Código" name="code" value={computed.code} readOnly={true} col={6} />

					<CustomInputSelect
						label="Aptitudes"
						name="aptitude"
						value={computed.selectedAptitudes.map((item) => item.value)}
						options={computed.selectedAptitudes}
						readOnly={true}
						col={6}
						isMulti
						classNamePrefix="react-select"
					/>

					<CustomInputSelect
						label="Ingredientes activos"
						name="active_ingredient"
						value={computed.selectedActiveIngredients.map((item) => item.value)}
						options={computed.selectedActiveIngredients}
						readOnly={true}
						col={6}
						isMulti
						classNamePrefix="react-select"
					/>

					<div className={styles.row}>
						<div className={styles.col} style={{ gridColumn: "1 / -1" }}>
							<div className={styles.viewerBar}>
								<div className={styles.label}>Etiqueta / Documento</div>
								<div className={styles.buttons}>
									{computed.url_tag ? (
										<>
											<a
												href={computed.url_tag}
												target="_blank"
												rel="noopener noreferrer"
												className={styles.link}
												title="Abrir en una pestaña nueva"
											>
												Abrir en nueva pestaña
											</a>
											<button type="button" className={styles.button} onClick={() => setShowViewer((prev) => !prev)}>
												{showViewer ? "Ocultar visor" : "Mostrar visor"}
											</button>
										</>
									) : (
										<span className={styles.muted}>Sin documento disponible</span>
									)}
								</div>
							</div>

							{computed.url_tag && showViewer && (
								<div className={styles.viewer}>
									<iframe src={computed.url_tag} title="Visor etiqueta o documento" className={styles.iframe} />
								</div>
							)}
						</div>
					</div>
				</>
			)}
		</CustomModalForm>
	);
};

export default ModalProductDetail;
